import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { listUsers, deleteUserById } from "../actions/userActions";
import Paginate from "../components/Paginate";

// Mirror the date format used elsewhere on the admin side.
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const UserListScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pageNumber } = useParams() || 1;

  const [search, setSearch] = useState("");
  const [showAdminsOnly, setShowAdminsOnly] = useState(false);

  const userList = useSelector((state) => state.userList);
  const { loading, error, users, page, userPages } = userList;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const userDelete = useSelector((state) => state.userDelete);
  const { success: successDelete } = userDelete;

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      navigate("/login");
      return;
    }
    if (search && search.length > 3) {
      dispatch(listUsers(false, search));
    } else if (showAdminsOnly) {
      dispatch(listUsers(true));
    } else {
      dispatch(listUsers(false, "", pageNumber));
    }
  }, [
    dispatch,
    navigate,
    userInfo,
    successDelete,
    showAdminsOnly,
    pageNumber,
    search,
  ]);

  const deleteHandler = (id, name) => {
    if (
      window.confirm(
        `¿Seguro que quieres eliminar a "${name}"? Esta acción no se puede deshacer.`
      )
    ) {
      dispatch(deleteUserById(id));
    }
  };

  return (
    <div className="hadacy-users">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Jost:wght@200;300;400;500&display=swap');

        /* ─── HADACY · admin user list ───
           Mirrors the customer-facing Mis Pedidos pattern: each user is
           a card, status pills on the left, action chips on the right.
           Cleaner than a Bootstrap table, easier to scan, plays nicely
           with dark mode through the same palette-handoff trick. */
        .hadacy-users {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;
          --recess: #efe7d8;
          --danger: #b04a3c;
          font-family: "Jost", sans-serif;
        }
        .hadacy-users-heading {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          letter-spacing: 0.04em;
          color: var(--dark);
          font-size: 2rem;
          margin: 0 0 0.25rem 0;
        }
        .hadacy-users-sub {
          font-size: 0.78rem;
          letter-spacing: 0.06em;
          color: var(--muted);
          margin: 0 0 1.5rem 0;
        }

        /* Filter strip */
        .hadacy-users-filters {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 1.2rem;
          padding: 0.8rem 1rem;
          background: var(--card-bg);
          border: 1px solid rgba(184, 147, 58, 0.18);
          border-radius: 12px;
        }
        .hadacy-users-search {
          flex: 1 1 240px;
          background: #fff;
          border: 1px solid rgba(184, 147, 58, 0.22);
          border-radius: 999px;
          padding: 0.55rem 0.95rem 0.55rem 2.1rem;
          font-family: "Jost", sans-serif;
          font-size: 0.88rem;
          color: var(--dark);
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23b8933a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/></svg>");
          background-repeat: no-repeat;
          background-position: 0.7rem center;
          background-size: 0.95rem;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .hadacy-users-search:focus {
          outline: none;
          border-color: var(--gold);
          box-shadow: 0 0 0 3px rgba(184, 147, 58, 0.14);
        }
        .hue-filter-toggle {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.5rem 0.95rem;
          border-radius: 999px;
          border: 1px solid rgba(184, 147, 58, 0.22);
          background: #fff;
          color: var(--dark);
          font-size: 0.78rem;
          cursor: pointer;
          user-select: none;
          transition: background .2s, border-color .2s, color .2s;
        }
        .hue-filter-toggle--on {
          background: rgba(184, 147, 58, 0.12);
          border-color: var(--gold);
          color: var(--gold);
        }
        .hue-filter-toggle input { display: none; }

        /* List */
        .hadacy-users-list {
          display: flex; flex-direction: column; gap: 0.85rem;
        }
        .hadacy-users-card {
          background: var(--card-bg);
          border: 1px solid rgba(184, 147, 58, 0.18);
          border-radius: 14px;
          padding: 1.05rem 1.2rem;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 0.85rem 1.1rem;
          align-items: center;
          transition: border-color .2s ease, box-shadow .2s ease,
                      transform .2s ease;
        }
        .hadacy-users-card:hover {
          border-color: rgba(184, 147, 58, 0.45);
          box-shadow: 0 10px 24px rgba(26, 20, 16, 0.08);
          transform: translateY(-1px);
        }
        .hadacy-users-card__head {
          grid-column: 1 / -1;
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem 1.2rem;
          padding-bottom: 0.7rem;
          border-bottom: 1px solid rgba(184, 147, 58, 0.14);
        }
        .hadacy-users-card__name {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.3rem;
          color: var(--dark);
          letter-spacing: 0.01em;
        }
        .hadacy-users-card__email {
          font-size: 0.85rem;
          color: var(--muted);
          letter-spacing: 0.02em;
          word-break: break-all;
        }
        .hadacy-users-card__email a { color: var(--muted); text-decoration: none; }
        .hadacy-users-card__email a:hover { color: var(--gold); }
        .hadacy-users-card__id {
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          opacity: 0.85;
        }
        .hadacy-users-card__joined {
          font-size: 0.7rem;
          letter-spacing: 0.04em;
          color: var(--muted);
        }

        /* Status pills */
        .hadacy-users-card__pills {
          grid-column: 1;
          display: flex; flex-wrap: wrap; gap: 0.45rem 0.55rem;
        }
        .hu-pill {
          display: inline-flex; align-items: center; gap: 0.45rem;
          padding: 0.32rem 0.75rem;
          border-radius: 999px;
          border: 1px solid;
          font-size: 0.72rem;
          letter-spacing: 0.04em;
        }
        .hu-pill i { font-size: 0.78rem; }
        .hu-pill--ok {
          background: rgba(184, 147, 58, 0.10);
          border-color: rgba(184, 147, 58, 0.45);
          color: var(--gold);
        }
        .hu-pill--neutral {
          background: var(--recess);
          border-color: rgba(122, 110, 98, 0.32);
          color: var(--muted);
        }
        .hu-pill--warn {
          background: rgba(176, 74, 60, 0.08);
          border-color: rgba(176, 74, 60, 0.4);
          color: var(--danger);
        }

        /* Action chips */
        .hadacy-users-card__actions {
          grid-column: 2; justify-self: end;
          display: flex; align-items: center; gap: 0.5rem;
        }
        .hu-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 0.45rem;
          padding: 0.5rem 0.95rem;
          border-radius: 999px;
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          text-decoration: none;
          border: 1px solid;
          cursor: pointer;
          background: transparent;
          transition: background .2s, color .2s, border-color .2s;
        }
        .hu-btn--primary {
          background: var(--dark);
          border-color: var(--dark);
          color: var(--gold-light);
        }
        .hu-btn--primary:hover {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--dark);
        }
        .hu-btn--ghost {
          color: var(--danger);
          border-color: rgba(176, 74, 60, 0.45);
        }
        .hu-btn--ghost:hover {
          background: rgba(176, 74, 60, 0.08);
          border-color: var(--danger);
        }

        /* Empty state */
        .hadacy-users-empty {
          background: var(--card-bg);
          border: 1px solid rgba(184, 147, 58, 0.18);
          border-radius: 14px;
          padding: 2.5rem 1.5rem;
          text-align: center;
          color: var(--muted);
        }
        .hadacy-users-empty i {
          font-size: 2rem; color: var(--gold); opacity: 0.6;
          margin-bottom: 0.9rem;
        }
        .hadacy-users-empty h3 {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400; font-size: 1.4rem; color: var(--dark);
          margin: 0 0 0.3rem 0;
        }

        /* Mobile: stack actions under pills */
        @media (max-width: 575.98px) {
          .hadacy-users-card { grid-template-columns: 1fr; }
          .hadacy-users-card__actions {
            grid-column: 1; justify-self: stretch;
          }
          .hu-btn { flex: 1; }
        }
      `}</style>

      <h1 className="hadacy-users-heading">Usuarios</h1>
      <p className="hadacy-users-sub">
        Administra cuentas registradas, restablece contraseñas y consulta el
        estado del correo de verificación.
      </p>

      <div className="hadacy-users-filters">
        <input
          type="text"
          className="hadacy-users-search"
          placeholder="Buscar por nombre o correo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label
          className={`hue-filter-toggle ${
            showAdminsOnly ? "hue-filter-toggle--on" : ""
          }`}
        >
          <i className="fas fa-shield-alt"></i>
          Solo admins
          <input
            type="checkbox"
            checked={showAdminsOnly}
            onChange={(e) => setShowAdminsOnly(e.target.checked)}
          />
        </label>
      </div>

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : !users || users.length === 0 ? (
        <div className="hadacy-users-empty">
          <i className="fas fa-users"></i>
          <h3>Sin resultados</h3>
          <p>No encontramos usuarios con esos criterios.</p>
        </div>
      ) : (
        <>
          <div className="hadacy-users-list">
            {users.map((u) => (
              <article key={u._id} className="hadacy-users-card">
                <div className="hadacy-users-card__head">
                  <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <div className="hadacy-users-card__name">{u.name}</div>
                    <div className="hadacy-users-card__email">
                      <a href={`mailto:${u.email}`}>{u.email}</a>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.15rem",
                      textAlign: "right",
                    }}
                  >
                    <span className="hadacy-users-card__id">
                      #{String(u._id).slice(-8).toUpperCase()}
                    </span>
                    <span className="hadacy-users-card__joined">
                      Registro {formatDate(u.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="hadacy-users-card__pills">
                  {u.isAdmin ? (
                    <span className="hu-pill hu-pill--ok">
                      <i className="fas fa-shield-alt"></i> Administrador
                    </span>
                  ) : (
                    <span className="hu-pill hu-pill--neutral">
                      <i className="fas fa-user"></i> Cliente
                    </span>
                  )}
                  {u.confirmed ? (
                    <span className="hu-pill hu-pill--ok">
                      <i className="fas fa-check-circle"></i> Verificado
                    </span>
                  ) : (
                    <span className="hu-pill hu-pill--warn">
                      <i className="far fa-clock"></i> Sin verificar
                    </span>
                  )}
                  {u.confirmationEmailSent ? (
                    <span
                      className="hu-pill hu-pill--neutral"
                      title={
                        u.confirmationEmailSentAt
                          ? `Enviado ${formatDate(u.confirmationEmailSentAt)}`
                          : "Correo enviado"
                      }
                    >
                      <i className="fas fa-envelope"></i> Email enviado
                    </span>
                  ) : (
                    <span
                      className="hu-pill hu-pill--warn"
                      title={
                        u.confirmationEmailError
                          ? `Error: ${u.confirmationEmailError}`
                          : "El correo de bienvenida nunca se envió"
                      }
                    >
                      <i className="fas fa-exclamation-circle"></i> Email no enviado
                    </span>
                  )}
                </div>

                <div className="hadacy-users-card__actions">
                  <Link
                    to={`/admin/user/${u._id}/edit`}
                    className="hu-btn hu-btn--primary"
                  >
                    <i className="fas fa-edit"></i> Editar
                  </Link>
                  <button
                    type="button"
                    className="hu-btn hu-btn--ghost"
                    onClick={() => deleteHandler(u._id, u.name)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </article>
            ))}
          </div>
          <Paginate
            pages={userPages}
            page={page}
            isAdmin={true}
            isUserList={true}
          />
        </>
      )}
    </div>
  );
};

export default UserListScreen;
